import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { colors } from "../theme";
import Header from "../components/Header";
import Badge from "../components/Badge";
import { formatTon, formatTimeLeft, formatCountdown } from "../hooks/useBets";
import type { Bet } from "../hooks/useBets";
import ProgressBar from "../components/ProgressBar";
import { useUser } from "../hooks/useAuth";
import type { User } from "../hooks/useAuth";
import type { NavigateFunction } from "react-router-dom";
import api from "../api";
import { cleanDescription } from "../utils/cleanDescription";

type DetailBet = Bet & {
  proposed_winner_id: number | null;
  proposed_winner_side: string | null;
  proposed_at: string | null;
};

interface Participant {
  user_id: number;
  telegram_id: number;
  display_name: string;
  amount: number;
  side: string;
  payout_amount?: number;
}

interface ParticipantsData {
  for_side: Participant[];
  against_side: Participant[];
  for_total: number;
  against_total: number;
}

export default function BetDetailPage() {
  const { betId } = useParams<{ betId: string }>();
  const navigate = useNavigate();
  const user = useUser();
  const [bet, setBet] = useState<DetailBet | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showLoadingTimeout, setShowLoadingTimeout] = useState(false);

  useEffect(() => {
    if (!betId) {
      setError("Bet not found");
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchBet = async () => {
      setLoading(true);
      setError(null);
      setBet(null);

      const getBet = async () => {
        const res = await api.get(`/bets/${betId}`, { timeout: 10000 });
        const d = res.data;
        if (d && typeof d === "object" && typeof d.status === "string") {
          return {
            ...d,
            bet_id: d.bet_id,
            amount_ton: typeof d.amount_ton === "number" ? d.amount_ton : d.amount / 1_000_000_000,
            challenger_name: d.challenger_name || "",
            challenger_telegram_id: d.challenger_telegram_id || d.creator_telegram_id || 0,
            proposed_winner_id: d.proposed_winner_id ?? null,
            proposed_winner_side: d.proposed_winner_side ?? null,
            proposed_at: d.proposed_at ?? null,
          } as DetailBet;
        }
        throw new Error("Invalid bet data");
      };

      // Retry up to 3 times (auth may not be ready when Mini App opens)
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          const normalizedBet = await getBet();
          if (!cancelled) {
            setBet(normalizedBet);
            setLoading(false);
          }
          return;
        } catch (err: any) {
          if (err?.response?.status === 401 && attempt < 2) {
            await new Promise((resolve) => setTimeout(resolve, 1500 * (attempt + 1)));
            continue;
          }
          if (!cancelled) {
            setError("Bet not found");
            setLoading(false);
          }
          return;
        }
      }
    };

    fetchBet();

    return () => {
      cancelled = true;
    };
  }, [betId]);

  useEffect(() => {
    if (!loading) {
      setShowLoadingTimeout(false);
      return;
    }

    const timeout = setTimeout(() => {
      setShowLoadingTimeout(true);
    }, 10_000);

    return () => clearTimeout(timeout);
  }, [loading]);

  if (loading) {
    return (
      <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Header title="Bet" showBack showClose />
        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: colors.grayLight, gap: 8 }}>
          <div>Loading...</div>
          {showLoadingTimeout ? <div style={{ fontSize: 13 }}>Taking too long? Try opening from the app.</div> : null}
        </div>
      </div>
    );
  }

  if (error || !bet) {
    return (
      <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Header title="Bet" showBack showClose />
        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 32, textAlign: "center" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
          <div style={{ fontSize: 16, color: colors.danger }}>{error || "Bet not found"}</div>
        </div>
      </div>
    );
  }

  if (bet.status?.toLowerCase() === "resolved") {
    return <ResolvedState bet={bet} user={user} navigate={navigate} betId={betId} />;
  }

  if (bet.status?.toLowerCase() === "live") {
    return <LiveState bet={bet} user={user} navigate={navigate} setBet={setBet} betId={betId} />;
  }

  if (bet.status?.toLowerCase() === "cancelled" || bet.status?.toLowerCase() === "expired") {
    return <CancelledExpiredState bet={bet} navigate={navigate} />;
  }

  // Default: WAITING (funded)
  return <WaitingState bet={bet} user={user} navigate={navigate} setBet={setBet} betId={betId} />;
}

/* ---------- CANCELLED / EXPIRED ---------- */

function CancelledExpiredState({ bet, navigate }: { bet: DetailBet; navigate: NavigateFunction }) {
  const isCancelled = bet.status?.toLowerCase() === "cancelled";
  const cleanedDescription = cleanDescription(bet.description);
  const emoji = getActivityEmoji(cleanedDescription);
  const amountTon = bet.amount / 1_000_000_000;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Header title="Bet Detail" showBack showClose onClose={() => navigate("/")} />
      <div style={{ flex: 1, overflow: "auto", padding: "0 16px 24px", display: "flex", flexDirection: "column", alignItems: "center" }}>
        <div style={{ marginBottom: 16 }}>
          <Badge status={isCancelled ? "cancelled" : "expired"} />
        </div>

        <div style={{ fontSize: 48, marginBottom: 12 }}>{isCancelled ? "\u{1F6AB}" : "\u23F0"}</div>
        <div style={{ fontSize: 20, fontWeight: 800, color: colors.white, marginBottom: 4 }}>
          {isCancelled ? "Bet Cancelled" : "Bet Expired"}
        </div>
        <div style={{ fontSize: 14, color: colors.grayLight, marginBottom: 24, textAlign: "center", lineHeight: 1.5 }}>
          {isCancelled
            ? "This bet was cancelled. Your stake has been refunded."
            : "No one accepted in time. Your stake has been auto-refunded."}
        </div>

        <div style={{
          width: "100%", background: colors.surface, border: `1px solid ${colors.border}`,
          borderRadius: 16, padding: 16, marginBottom: 24,
        }}>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>{emoji} {cleanedDescription}</div>
          <div style={{ fontSize: 13, color: colors.grayLight }}>
            {amountTon % 1 === 0 ? amountTon.toFixed(0) : amountTon.toFixed(1)} TON — refunded
          </div>
        </div>

        <div style={{ flex: 1 }} />

        <button
          onClick={() => navigate("/")}
          style={{
            width: "100%", padding: "14px 0", borderRadius: 14, border: "none",
            background: colors.primary, color: colors.bg,
            fontSize: 15, fontWeight: 700, cursor: "pointer",
          }}
        >
          Back to Home
        </button>
      </div>
    </div>
  );
}


/* ---------- WAITING (funded) ---------- */

interface BetStateProps {
  bet: DetailBet;
  user?: User | null;
  navigate: NavigateFunction;
  setBet?: (bet: DetailBet | null) => void;
  betId?: string;
}

function useParticipants(bet: Bet, betId?: string) {
  const [participants, setParticipants] = useState<ParticipantsData | null>(null);
  useEffect(() => {
    if (bet.bet_mode !== "pile_on" || !betId) return;
    api.get(`/bets/${betId}/participants`).then((res) => setParticipants(res.data)).catch(() => {});
  }, [bet.bet_mode, betId]);
  return participants;
}

function WaitingState({ bet, user, navigate, setBet, betId }: BetStateProps) {
  const [countdown, setCountdown] = useState(formatCountdown(bet.expires_at));
  // VEN-38: separate timeLeft state — the inline formatTimeLeft string in
  // the BOTH AGREE / STRAVA AUTO row used to be frozen at first render.
  const [timeLeft, setTimeLeft] = useState(formatTimeLeft(bet.expires_at));
  const [cancelling, setCancelling] = useState(false);
  const [locking, setLocking] = useState(false);
  const participants = useParticipants(bet, betId);
  const isPileOn = bet.bet_mode === "pile_on";
  const isDare = isPileOn && bet.pile_on_type === "dare";
  const isCreator = bet.challenger_telegram_id === user?.telegram_id || bet.creator_telegram_id === user?.telegram_id;
  const isDareTarget = isDare && bet.target_user_id === user?.id;
  const isOpponent = !isCreator && (bet.opponent_telegram_id === user?.telegram_id);
  const isParticipant = isPileOn && participants && user
    ? [...participants.for_side, ...participants.against_side].some(
        (p) => p.telegram_id === user.telegram_id
      )
    : isCreator;

  // Live countdown every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(formatCountdown(bet.expires_at));
      setTimeLeft(formatTimeLeft(bet.expires_at));
    }, 1000);
    return () => clearInterval(timer);
  }, [bet.expires_at]);

  const handleCancel = async () => {
    if (cancelling) return;
    if (bet.status?.toLowerCase() !== "funded") return; // VEN-31 defensive guard
    setCancelling(true);
    try {
      await api.post(`/bets/${bet.bet_id}/cancel`);
      // VEN-31: update local state immediately so the button disappears and
      // the CancelledExpiredState kicks in on the next render, even before
      // navigation completes. Prevents double-tap 400 "already cancelled".
      if (setBet) setBet({ ...bet, status: "cancelled" } as DetailBet);
      navigate("/");
    } catch {
      setCancelling(false);
    }
  };

  const handleShare = async () => {
    // VEN-37: backend returns {deep_link, text, ...}, not {url}. The old
    // `res.data?.url || res.data?.link || res.data` guard landed on the raw
    // object (truthy, non-string) so the typeof check silently did nothing
    // and the Share Challenge button felt dead. Build the Telegram share
    // URL ourselves from deep_link + text and open it via the WebApp API.
    const compactId = bet.bet_id.replace(/-/g, "");
    const fallbackDeep = `https://t.me/sidebets_dev_bot/app?startapp=bet_${compactId}`;
    let deepLink = fallbackDeep;
    let shareText = `🎲 I bet ${bet.description}! Prove me wrong!`;
    try {
      const res = await api.get(`/bets/${bet.bet_id}/share`);
      if (typeof res.data?.deep_link === "string") deepLink = res.data.deep_link;
      if (typeof res.data?.text === "string") shareText = res.data.text;
    } catch {
      // Use the fallback built above.
    }
    const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(deepLink)}&text=${encodeURIComponent(shareText)}`;
    const tg = window.Telegram?.WebApp;
    if (tg?.openTelegramLink) {
      tg.openTelegramLink(shareUrl);
    } else if (navigator.share) {
      navigator.share({ url: deepLink, text: shareText }).catch(() => {
        navigator.clipboard.writeText(deepLink);
      });
    } else {
      navigator.clipboard.writeText(deepLink);
    }
  };

  const cleanedDescription = cleanDescription(bet.description);
  const emoji = getActivityEmoji(cleanedDescription);
  const isFitness = bet.strava_bet_type !== null;

  const handleLock = async () => {
    if (locking) return;
    setLocking(true);
    try {
      const res = await api.post(`/bets/${bet.bet_id}/lock`);
      if (setBet && res.data) {
        setBet({ ...bet, ...res.data, amount_ton: bet.amount_ton });
      }
    } catch {
      setLocking(false);
    }
  };

  const needsTarget = bet.pile_on_type === "dare" || bet.pile_on_type === "versus";
  const targetOk = !needsTarget || (bet as any).target_accepted;
  const canLock = isPileOn && isCreator && (bet.for_count || 0) >= 1 && (bet.against_count || 0) >= 1 && targetOk;
  const joinWindowLeft = bet.join_window_closes_at ? formatCountdown(bet.join_window_closes_at) : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Header title="Bet Detail" showBack showClose onClose={() => navigate("/")} />

      <div style={{ flex: 1, overflow: "auto", padding: "0 16px 24px", display: "flex", flexDirection: "column", alignItems: "center" }}>
        {/* Badge */}
        <div style={{ marginBottom: 16 }}>
          <Badge status={isPileOn ? "funded" : "funded"} />
        </div>

        {/* Bet card */}
        <div style={{
          width: "100%", background: colors.surface, border: `1px solid ${colors.border}`,
          borderRadius: 16, padding: 16, marginBottom: 24,
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <span style={{ fontSize: 15, fontWeight: 600 }}>{emoji} {cleanedDescription}</span>
            <span style={{ fontSize: 11, fontWeight: 700, color: isFitness ? colors.success : colors.grayLight, textTransform: "uppercase" as const }}>
              {isFitness ? "HEALTHBRIDGE AUTO ✓" : "BOTH AGREE"}
            </span>
          </div>
          <div style={{ display: "flex", gap: 12, fontSize: 13, color: colors.grayLight }}>
            <span>⏰ {timeLeft}</span>
            {isPileOn && joinWindowLeft && (
              <span>🕐 Join: {joinWindowLeft} left</span>
            )}
          </div>
        </div>

        {isPileOn ? (
          <>
            {/* Pot display */}
            <div style={{ fontSize: 42, fontWeight: 800, color: colors.white, marginBottom: 4 }}>
              {((bet.total_pot || bet.amount) / 1_000_000_000).toFixed(1)} TON
            </div>
            <div style={{ fontSize: 14, color: colors.grayLight, marginBottom: 24 }}>
              total pot
            </div>

            {/* Participants list */}
            {participants && (
              <ParticipantsList participants={participants} betStatus={bet.status} />
            )}
          </>
        ) : (
          <>
            {/* Hero amount */}
            <div style={{ fontSize: 42, fontWeight: 800, color: colors.white, marginBottom: 4 }}>
              {formatTon(bet.amount)}
            </div>
            <div style={{ fontSize: 14, color: colors.grayLight, marginBottom: 24 }}>
              locked by you
            </div>
          </>
        )}

        {/* Countdown chip */}
        <div style={{
          background: `${colors.amber}18`, padding: "8px 16px", borderRadius: 10,
          fontSize: 14, fontWeight: 600, color: colors.amber, marginBottom: 8,
        }}>
          ⏰ Expires in {countdown}
        </div>
        <div style={{ fontSize: 13, color: colors.grayLight, marginBottom: isPileOn && isFitness ? 8 : 32 }}>
          {isPileOn ? "Join window closes → bet goes LIVE" : "No one accepts → auto-refund"}
        </div>
        {isPileOn && isFitness && (
          <div style={{ fontSize: 12, color: colors.gray, marginBottom: 32 }}>
            📊 Fitness tracking starts when bet goes LIVE
          </div>
        )}

        {/* Spacer */}
        <div style={{ flex: 1 }} />

        {isPileOn ? (
          <>
            {/* Lock & Start (creator only, both sides have participants) */}
            {canLock && (
              <button onClick={handleLock} disabled={locking} style={{
                width: "100%", padding: "14px 0", borderRadius: 14, border: "none",
                background: colors.purple, color: colors.white,
                fontSize: 15, fontWeight: 700, marginBottom: 12, cursor: "pointer",
                opacity: locking ? 0.5 : 1,
              }}>
                {locking ? "Locking..." : "🔒 Lock & Start"}
              </button>
            )}

            {/* Join buttons (non-participant). Wait for BOTH participants
                and the user to load — otherwise, while `user` is still null
                from AuthContext, `isParticipant` falls back to `isCreator`
                (which evaluates false against an undefined telegram_id) and
                the Join buttons flash for a frame before hiding (VEN-24). */}
            {!isParticipant && participants && user && (
              <div style={{ display: "flex", gap: 8, width: "100%", marginBottom: 12 }}>
                <button onClick={() => navigate(`/join/${bet.bet_id}?side=for`)} style={{
                  flex: 1, padding: "14px 0", borderRadius: 14, border: "none",
                  background: colors.primary, color: colors.bg,
                  fontSize: 15, fontWeight: 700, cursor: "pointer",
                }}>
                  💪 Join FOR
                </button>
                <button onClick={() => navigate(`/join/${bet.bet_id}?side=against`)} style={{
                  flex: 1, padding: "14px 0", borderRadius: 14, border: "none",
                  background: colors.danger, color: colors.white,
                  fontSize: 15, fontWeight: 700, cursor: "pointer",
                }}>
                  😈 Join AGAINST
                </button>
              </div>
            )}

            {/* Share + Cancel (creator) */}
            {isCreator && (
              <>
                <button onClick={handleShare} style={{
                  width: "100%", padding: "14px 0", borderRadius: 14, border: "none",
                  background: colors.surfaceLight, color: colors.white,
                  fontSize: 15, fontWeight: 700, marginBottom: 12, cursor: "pointer",
                }}>
                  📤 Share
                </button>
                {bet.status?.toLowerCase() === "funded" && (
                  <button onClick={handleCancel} disabled={cancelling} style={{
                    width: "100%", padding: "12px 0", borderRadius: 14,
                    background: "transparent", border: `1px solid ${colors.danger}`,
                    color: colors.danger, fontSize: 14, fontWeight: 600, cursor: "pointer",
                    opacity: cancelling ? 0.5 : 1,
                  }}>
                    {cancelling ? "Cancelling..." : "Cancel Bet (auto-refund)"}
                  </button>
                )}
              </>
            )}
          </>
        ) : (
          <>
            {isOpponent ? (
              <button onClick={() => navigate(`/accept/${bet.bet_id}`)} style={{
                width: "100%", padding: "14px 0", borderRadius: 14, border: "none",
                background: colors.primary, color: colors.bg,
                fontSize: 15, fontWeight: 700, marginBottom: 12, cursor: "pointer",
              }}>
                ⚔️ Accept Challenge
              </button>
            ) : isCreator ? (
              <>
                <button onClick={handleShare} style={{
                  width: "100%", padding: "14px 0", borderRadius: 14, border: "none",
                  background: colors.purple, color: colors.white,
                  fontSize: 15, fontWeight: 700, marginBottom: 12, cursor: "pointer",
                }}>
                  📤 Share Challenge
                </button>
                {bet.status?.toLowerCase() === "funded" && (
                  <button onClick={handleCancel} disabled={cancelling} style={{
                    width: "100%", padding: "12px 0", borderRadius: 14,
                    background: "transparent", border: `1px solid ${colors.danger}`,
                    color: colors.danger, fontSize: 14, fontWeight: 600, cursor: "pointer",
                    opacity: cancelling ? 0.5 : 1,
                  }}>
                    {cancelling ? "Cancelling..." : "Cancel Bet (auto-refund)"}
                  </button>
                )}
              </>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

/* ---------- LIVE ---------- */

interface ActivityEntry {
  date: string;
  time?: string;
  value: number;
  name: string;
}

interface VersusProgress {
  challenger: {
    current_value: number;
    target_value: number;
    progress_pct: number;
    unit: string;
    display_name: string;
    user_id: number;
  };
  opponent: {
    current_value: number;
    target_value: number;
    progress_pct: number;
    unit: string;
    display_name: string;
    user_id: number;
  };
}

function LiveState({ bet, user, navigate, setBet, betId }: BetStateProps) {
  const [progress, setProgress] = useState<{
    current_value: number;
    target_value: number;
    percentage: number;
    last_synced?: string;
    activities?: ActivityEntry[];
    unit?: string;
  } | null>(null);
  const [versusProgress, setVersusProgress] = useState<VersusProgress | null>(null);
  const [timeLeft, setTimeLeft] = useState(formatTimeLeft(bet.expires_at));
  // VEN-99: check HealthBridge connection status for fitness bets
  const [hbConnected, setHbConnected] = useState<boolean | null>(null);
  useEffect(() => {
    if (!bet.strava_bet_type) return;
    api.get("/healthbridge/status")
      .then((res) => setHbConnected(res.data?.connected ?? false))
      .catch(() => setHbConnected(false));
  }, [bet.strava_bet_type]);

  useEffect(() => {
    // VEN-38: tick every second so the minute+second countdown stays live
    // in the final hour. 1s overhead is negligible for a single countdown.
    const timer = setInterval(() => setTimeLeft(formatTimeLeft(bet.expires_at)), 1_000);
    return () => clearInterval(timer);
  }, [bet.expires_at]);

  useEffect(() => {
    if (bet.status?.toLowerCase() !== "live" || !bet.strava_bet_type) return;

    const fitnessProvider =
      (import.meta.env.VITE_FITNESS_PROVIDER as string | undefined) || "healthbridge";
    api
      .get(`/fitness/${fitnessProvider}/check-bet/${bet.bet_id}`)
      .then((res) => {
        const d = res.data || {};

        // VEN-114/VEN-116: versus bets return both players' progress
        if (d.versus && d.challenger && d.opponent) {
          setVersusProgress({
            challenger: {
              current_value: Number(d.challenger.current_value ?? 0),
              target_value: Number(d.challenger.target_value ?? bet.strava_target_value ?? 0),
              progress_pct: Number(d.challenger.progress_pct ?? 0),
              unit: d.challenger.unit || "",
              display_name: d.challenger.display_name || "Creator",
              user_id: d.challenger.user_id,
            },
            opponent: {
              current_value: Number(d.opponent.current_value ?? 0),
              target_value: Number(d.opponent.target_value ?? bet.strava_target_value ?? 0),
              progress_pct: Number(d.opponent.progress_pct ?? 0),
              unit: d.opponent.unit || "",
              display_name: d.opponent.display_name || "Opponent",
              user_id: d.opponent.user_id,
            },
          });
        }

        const percentage = Number(d.percentage ?? d.progress_pct ?? 0);
        const currentValue = Number(d.current_value ?? 0);
        const targetValue = Number(d.target_value ?? bet.strava_target_value ?? 0);
        const rawActivities: Record<string, unknown>[] = Array.isArray(d.activities) ? d.activities : [];
        const activities: ActivityEntry[] = rawActivities
          .filter((a: Record<string, unknown>) => a && typeof a.date === "string" && typeof a.value === "number")
          .map((a: Record<string, unknown>) => ({
            date: a.date as string,
            time: typeof a.time === "string" ? a.time : undefined,
            value: a.value as number,
            name: typeof a.name === "string" ? a.name : "",
          }));
        setProgress({
          current_value: currentValue,
          target_value: targetValue,
          percentage,
          last_synced: d.last_synced || new Date().toISOString(),
          activities,
          unit: typeof d.unit === "string" ? d.unit : undefined,
        });
      })
      .catch(() => {
        setProgress(null);
      });
  }, [bet.bet_id, bet.status, bet.strava_bet_type, bet.strava_target_value]);

  const pct = progress?.percentage ?? 0;
  const current = progress?.current_value ?? 0;
  const target = progress?.target_value ?? bet.strava_target_value ?? 0;
  const showSyncing = !progress;
  const syncedMinutesAgo = progress?.last_synced
    ? Math.max(0, Math.floor((Date.now() - new Date(progress.last_synced).getTime()) / 60000))
    : null;
  const isPileOn = bet.bet_mode === "pile_on";
  const participants = useParticipants(bet, betId);
  const potTon = isPileOn
    ? (bet.total_pot || bet.amount) / 1_000_000_000
    : (bet.amount * 2) / 1_000_000_000;
  const potDisplay = potTon % 1 === 0 ? potTon.toFixed(1) : potTon.toString();
  const totalPeople = isPileOn ? (bet.for_count || 0) + (bet.against_count || 0) : 2;
  const cleanedDescription = cleanDescription(bet.description);
  const emoji = getActivityEmoji(cleanedDescription);
  const unit = progress?.unit || getUnit(bet.strava_bet_type);
  const isVersus = bet.pile_on_type === "versus" && !!versusProgress;
  const isCreator = user?.telegram_id === bet.challenger_telegram_id || user?.id === (bet as any).challenger_id;
  const isDare = (isPileOn && bet.pile_on_type === "dare") || bet.bet_type === "dare";
  const isDareTarget = isDare && bet.target_user_id === user?.id;
  const opponentName = isCreator
    ? (bet.opponent_name || "opponent").replace(/^@/, "")
    : (bet.challenger_name || "challenger").replace(/^@/, "");
  const [proposing, setProposing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [forceResolving, setForceResolving] = useState(false);
  const [forceResolveMsg, setForceResolveMsg] = useState<string | null>(null);

  const forceResolveStrava = async () => {
    if (!betId || forceResolving) return;
    setForceResolving(true);
    setForceResolveMsg(null);
    try {
      const res = await api.post(`/bets/admin/strava-resolve`);
      const data = res.data || {};
      const resolved = Array.isArray(data.resolved) ? data.resolved.length : data.resolved ?? 0;
      setForceResolveMsg(`Check triggered. Resolved: ${resolved}. Refresh to see updated status.`);
      // Refresh the bet after a short delay to let DB commit settle.
      setTimeout(() => { refreshBet().catch(() => {}); }, 1500);
    } catch (err: any) {
      setForceResolveMsg(err.response?.data?.detail || "Force resolve failed");
    } finally {
      setForceResolving(false);
    }
  };

  const refreshBet = async () => {
    if (!betId || !setBet) return;
    const res = await api.get(`/bets/${betId}`);
    const d = res.data || {};
    setBet({
      ...bet,
      ...d,
      amount_ton: typeof d.amount_ton === "number" ? d.amount_ton : (d.amount ?? bet.amount) / 1_000_000_000,
      proposed_winner_id: d.proposed_winner_id ?? null,
      proposed_winner_side: d.proposed_winner_side ?? null,
      proposed_at: d.proposed_at ?? null,
    } as DetailBet);
  };

  const proposeResult = async (result: "completed" | "failed") => {
    if (!betId) return;
    setProposing(true);
    try {
      await api.post(`/bets/${betId}/propose-result`, { result });
      await refreshBet();
    } catch (err: any) {
      alert(err.response?.data?.detail || "Failed to propose result");
    } finally {
      setProposing(false);
    }
  };

  const confirmResult = async (action: "confirm" | "dispute") => {
    if (!betId) return;
    setConfirming(true);
    try {
      await api.post(`/bets/${betId}/confirm-result`, { action });
      await refreshBet();
    } catch (err: any) {
      alert(err.response?.data?.detail || "Failed to confirm result");
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Header title="Bet Detail" showBack showClose onClose={() => navigate("/")} />

      <div style={{ flex: 1, overflow: "auto", padding: "0 16px 24px" }}>
        {/* Badge */}
        <div style={{ textAlign: "center", marginBottom: 16 }}>
          <Badge status="live" />
        </div>

        {/* Hero percentage — only for Strava-tracked bets */}
        {bet.strava_bet_type && isVersus ? (
          /* VEN-114/VEN-116: versus bet — side-by-side progress */
          <>
            <div style={{ textAlign: "center", marginBottom: 8 }}>
              <div style={{ fontSize: 15, color: colors.grayLight }}>
                {emoji} {cleanedDescription}
              </div>
            </div>
            <VersusProgressDisplay
              challenger={versusProgress!.challenger}
              opponent={versusProgress!.opponent}
              unit={versusProgress!.challenger.unit || unit}
            />
          </>
        ) : bet.strava_bet_type ? (
          <>
            <div style={{ textAlign: "center", marginBottom: 8 }}>
              <div style={{ fontSize: 64, fontWeight: 800, color: colors.white, lineHeight: 1 }}>
                {showSyncing ? "Syncing..." : `${Math.round(pct)}%`}
              </div>
              <div style={{ fontSize: 15, color: colors.grayLight, marginTop: 4 }}>
                {emoji} {cleanedDescription}
              </div>
            </div>

            {/* Progress bar */}
            <div style={{ marginBottom: 24 }}>
              <ProgressBar
                percentage={pct}
                leftLabel={showSyncing ? "Syncing..." : `${current} / ${target} ${unit}`}
                rightLabel=""
              />
            </div>
          </>
        ) : (
          <div style={{ textAlign: "center", marginBottom: 24 }}>
            <div style={{ fontSize: 20, fontWeight: 700, color: colors.white, marginBottom: 4 }}>
              {emoji} {cleanedDescription}
            </div>
            <div style={{ fontSize: 13, color: colors.grayLight }}>
              Waiting for both sides to agree on the result
            </div>
          </div>
        )}

        {/* Activity chart — skip for versus (we show inline progress instead) */}
        {bet.strava_bet_type && !isVersus && (
          <ActivityChart
            activities={progress?.activities ?? []}
            startDate={bet.created_at}
            expiresAt={bet.expires_at}
            targetValue={target}
            unit={unit}
          />
        )}

        {/* 3 info chips */}
        <div style={{
          display: "grid", gridTemplateColumns: isPileOn ? "1fr 1fr 1fr" : "1fr 1fr 1fr", gap: 8, marginBottom: 24,
        }}>
          <InfoChip emoji="💰" label={`${potDisplay} TON`} sub="Pot" />
          <InfoChip emoji="⏰" label={timeLeft} sub="Left" />
          {isPileOn ? (
            <InfoChip emoji="👥" label={`${totalPeople}`} sub="People" />
          ) : bet.strava_bet_type ? (
            <InfoChip
              emoji="🔄"
              label={showSyncing ? "Syncing..." : `Synced ${syncedMinutesAgo ?? 0} min ago`}
              sub="Fitness"
            />
          ) : (
            <InfoChip emoji="👥" label="2" sub="People" />
          )}
        </div>

        {/* VEN-99: HealthBridge connection banner for fitness bets */}
        {bet.strava_bet_type && hbConnected === false && (
          <div style={{
            background: "rgba(252,163,17,0.1)",
            border: "1px solid rgba(252,163,17,0.3)",
            borderRadius: 12,
            padding: "12px 16px",
            marginBottom: 16,
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}>
            <span style={{ fontSize: 20, flexShrink: 0 }}>{"\u26A0\uFE0F"}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#FCA311", marginBottom: 4 }}>
                Connect HealthBridge
              </div>
              <div style={{ fontSize: 12, color: colors.grayLight, lineHeight: 1.4 }}>
                Auto-verification needs HealthBridge connected. Go to Profile to set it up.
              </div>
            </div>
            <button
              onClick={() => navigate("/profile")}
              style={{
                background: "#FCA311",
                border: "none",
                borderRadius: 8,
                padding: "6px 12px",
                fontSize: 12,
                fontWeight: 700,
                color: "#000",
                cursor: "pointer",
                whiteSpace: "nowrap" as const,
                flexShrink: 0,
              }}
            >
              Connect
            </button>
          </div>
        )}

        {/* Participants / VS section */}
        {isPileOn && participants ? (
          <ParticipantsList participants={participants} betStatus={bet.status} />
        ) : !isPileOn ? (
          <div style={{
            background: colors.surface, border: `1px solid ${colors.border}`,
            borderRadius: 16, padding: 16, marginBottom: 16,
          }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: colors.gray, textTransform: "uppercase" as const, marginBottom: 12 }}>VS</div>

            {/* You */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <div style={{
                width: 32, height: 32, borderRadius: 16, background: colors.purple,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 14, fontWeight: 700, color: colors.white,
              }}>
                {(user?.first_name || "Y").charAt(0).toUpperCase()}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>YOU</div>
                  {/* VEN-43: initiator chip — the user who opened the bet */}
                  {isCreator && (
                    <div style={{
                      fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
                      padding: "2px 6px", borderRadius: 6,
                      background: `${colors.purple}22`, color: colors.purple,
                      textTransform: "uppercase" as const,
                    }}>
                      ✦ Creator
                    </div>
                  )}
                </div>
                <div style={{ fontSize: 12, color: colors.grayLight }}>
                  {bet.strava_bet_type ? `${current} ${unit}, tracking` : "Challenger 💪"}
                </div>
              </div>
              {/* VEN-43: per-user stake */}
              <div style={{ fontSize: 12, fontWeight: 700, color: colors.primary }}>
                {formatTon(bet.amount)}
              </div>
            </div>

            {/* Opponent */}
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{
                width: 32, height: 32, borderRadius: 16, background: colors.surfaceLight,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 14, fontWeight: 700, color: colors.grayLight,
              }}>
                {opponentName.charAt(0).toUpperCase()}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>@{opponentName}</div>
                  {!isCreator && (
                    <div style={{
                      fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
                      padding: "2px 6px", borderRadius: 6,
                      background: `${colors.purple}22`, color: colors.purple,
                      textTransform: "uppercase" as const,
                    }}>
                      ✦ Creator
                    </div>
                  )}
                </div>
                <div style={{ fontSize: 12, color: colors.grayLight }}>Thinks you'll fail 😈</div>
              </div>
              <div style={{ fontSize: 12, fontWeight: 700, color: colors.primary }}>
                {formatTon(bet.amount)}
              </div>
            </div>
          </div>
        ) : null}

        {/* Last activity — only for fitness-tracked bets */}
        {bet.strava_bet_type && (
          <div style={{
            display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: colors.grayLight,
          }}>
            <span style={{ width: 6, height: 6, borderRadius: 3, background: colors.strava, display: "inline-block" }} />
            Last activity: {current} {unit}
          </div>
        )}

        {/* Force Resolve (QA) — creator only, fitness bets only.
            Triggers the admin/strava-resolve endpoint that the hourly cron
            runs automatically, so QA can verify end-to-end flow without
            waiting 30-60 min (VEN-26). */}
        {bet.status?.toLowerCase() === "live" && bet.strava_bet_type && isCreator && (
          <div style={{
            background: colors.surface,
            border: `1px dashed ${colors.border}`,
            borderRadius: 16,
            padding: 16,
            marginTop: 16,
            textAlign: "center",
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: colors.gray, textTransform: "uppercase", marginBottom: 8, letterSpacing: 0.5 }}>
              QA Tools
            </div>
            <button
              onClick={forceResolveStrava}
              disabled={forceResolving}
              style={{
                width: "100%", padding: "12px 0", borderRadius: 12, border: `1px solid ${colors.border}`,
                background: "transparent", color: colors.white,
                fontSize: 14, fontWeight: 600, cursor: "pointer",
                opacity: forceResolving ? 0.6 : 1,
              }}
            >
              {forceResolving ? "Checking HealthBridge..." : "\uD83D\uDD27 Force Resolve Now"}
            </button>
            {forceResolveMsg && (
              <div style={{ fontSize: 12, color: colors.grayLight, marginTop: 8, lineHeight: 1.4 }}>
                {forceResolveMsg}
              </div>
            )}
          </div>
        )}

        {bet.status?.toLowerCase() === "live" && (!bet.strava_bet_type || isDare) && (
          <div style={{
            background: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: 16,
            padding: 16,
            marginTop: 16,
          }}>
            {!bet.proposed_winner_side && (
              isDare ? (
                isDareTarget ? (
                  <>
                    <div style={{ fontSize: 14, fontWeight: 700, color: colors.grayLight, textTransform: "uppercase", marginBottom: 12, textAlign: "center" }}>
                      Did you complete the dare?
                    </div>
                    <div style={{ display: "flex", gap: 12 }}>
                      <button onClick={() => proposeResult("completed")} disabled={proposing}
                        style={{ flex: 1, padding: "14px 0", borderRadius: 12, border: "none", background: "rgba(34,197,94,0.15)", color: colors.success, fontSize: 15, fontWeight: 700, cursor: "pointer", opacity: proposing ? 0.6 : 1 }}>
                        ✅ Completed
                      </button>
                      <button onClick={() => proposeResult("failed")} disabled={proposing}
                        style={{ flex: 1, padding: "14px 0", borderRadius: 12, border: "none", background: "rgba(239,68,68,0.15)", color: colors.danger, fontSize: 15, fontWeight: 700, cursor: "pointer", opacity: proposing ? 0.6 : 1 }}>
                        ❌ Failed
                      </button>
                    </div>
                  </>
                ) : (
                  <div style={{ textAlign: "center", color: colors.grayLight, fontSize: 14 }}>
                    ⏳ Waiting for the challenged person to report the result...
                  </div>
                )
              ) : (
                <>
                  <div style={{ fontSize: 14, fontWeight: 700, color: colors.grayLight, textTransform: "uppercase", marginBottom: 12, textAlign: "center" }}>
                    How did it go?
                  </div>
                  <div style={{ display: "flex", gap: 12 }}>
                    <button onClick={() => proposeResult("completed")} disabled={proposing}
                      style={{ flex: 1, padding: "14px 0", borderRadius: 12, border: "none", background: "rgba(34,197,94,0.15)", color: colors.success, fontSize: 15, fontWeight: 700, cursor: "pointer", opacity: proposing ? 0.6 : 1 }}>
                      ✅ Completed
                    </button>
                    <button onClick={() => proposeResult("failed")} disabled={proposing}
                      style={{ flex: 1, padding: "14px 0", borderRadius: 12, border: "none", background: "rgba(239,68,68,0.15)", color: colors.danger, fontSize: 15, fontWeight: 700, cursor: "pointer", opacity: proposing ? 0.6 : 1 }}>
                      ❌ Failed
                    </button>
                  </div>
                </>
              )
            )}

            {bet.proposed_winner_side && (
              isDare ? (
                isCreator ? (
                  <>
                    <div style={{ textAlign: "center", marginBottom: 12 }}>
                      <div style={{ fontSize: 14, color: colors.grayLight, marginBottom: 4 }}>
                        The challenged person says:
                      </div>
                      <div style={{ fontSize: 18, fontWeight: 800 }}>
                        {bet.proposed_winner_side === "for" ? "✅ Completed" : "❌ Failed"}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 12 }}>
                      <button onClick={() => confirmResult("confirm")} disabled={confirming}
                        style={{ flex: 1, padding: "14px 0", borderRadius: 12, border: "none", background: colors.primary, color: "#000", fontSize: 15, fontWeight: 700, cursor: "pointer", opacity: confirming ? 0.6 : 1 }}>
                        ✅ Confirm
                      </button>
                      <button onClick={() => confirmResult("dispute")} disabled={confirming}
                        style={{ flex: 1, padding: "14px 0", borderRadius: 12, border: "none", background: "rgba(239,68,68,0.15)", color: colors.danger, fontSize: 15, fontWeight: 700, cursor: "pointer", opacity: confirming ? 0.6 : 1 }}>
                        ⚖️ Dispute
                      </button>
                    </div>
                  </>
                ) : (
                  <div style={{ textAlign: "center", color: colors.grayLight, fontSize: 14 }}>
                    ⏳ Waiting for the dare creator to confirm...
                  </div>
                )
              ) : (
                <>
                  {bet.proposed_winner_id === user?.id && (
                    <div style={{ textAlign: "center", color: colors.grayLight, fontSize: 14 }}>
                      ⏳ You proposed a result. Waiting for the other side to confirm...
                    </div>
                  )}
                  {bet.proposed_winner_id && bet.proposed_winner_id !== user?.id && (
                    <>
                      <div style={{ textAlign: "center", marginBottom: 12 }}>
                        <div style={{ fontSize: 14, color: colors.grayLight, marginBottom: 4 }}>
                          Other side says:
                        </div>
                        <div style={{ fontSize: 18, fontWeight: 800 }}>
                          {bet.proposed_winner_side === "for" ? "✅ Completed" : "❌ Failed"}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 12 }}>
                        <button onClick={() => confirmResult("confirm")} disabled={confirming}
                          style={{ flex: 1, padding: "14px 0", borderRadius: 12, border: "none", background: colors.primary, color: "#000", fontSize: 15, fontWeight: 700, cursor: "pointer", opacity: confirming ? 0.6 : 1 }}>
                          ✅ Confirm
                        </button>
                        <button onClick={() => confirmResult("dispute")} disabled={confirming}
                          style={{ flex: 1, padding: "14px 0", borderRadius: 12, border: "none", background: "rgba(239,68,68,0.15)", color: colors.danger, fontSize: 15, fontWeight: 700, cursor: "pointer", opacity: confirming ? 0.6 : 1 }}>
                          ⚖️ Dispute
                        </button>
                      </div>
                    </>
                  )}
                </>
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- VERSUS PROGRESS DISPLAY (VEN-114/VEN-116) ---------- */

function VersusProgressDisplay({
  challenger,
  opponent,
  unit,
}: {
  challenger: VersusProgress["challenger"];
  opponent: VersusProgress["opponent"];
  unit: string;
}) {
  const cPct = Math.round(challenger.progress_pct);
  const oPct = Math.round(opponent.progress_pct);
  const challengerLeading = challenger.current_value > opponent.current_value;
  const opponentLeading = opponent.current_value > challenger.current_value;
  const tied = challenger.current_value === opponent.current_value;

  return (
    <div style={{
      width: "100%",
      background: colors.surface,
      border: `1px solid ${colors.border}`,
      borderRadius: 16,
      padding: 16,
      marginBottom: 24,
    }}>
      {/* Header */}
      <div style={{
        fontSize: 12, fontWeight: 700, color: colors.gray,
        textTransform: "uppercase" as const, letterSpacing: 0.5,
        marginBottom: 16, textAlign: "center",
      }}>
        {tied ? "TIED" : challengerLeading ? `${challenger.display_name} LEADING` : `${opponent.display_name} LEADING`}
      </div>

      {/* Side by side */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        {/* Challenger */}
        <div style={{
          background: challengerLeading ? "rgba(173,255,0,0.08)" : "rgba(255,255,255,0.02)",
          borderRadius: 14, padding: 14, textAlign: "center",
          border: challengerLeading ? `1px solid ${colors.primary}33` : "1px solid transparent",
        }}>
          <div style={{
            width: 40, height: 40, borderRadius: 20,
            background: challengerLeading ? colors.primary : colors.surfaceLight,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 16, fontWeight: 700,
            color: challengerLeading ? colors.bg : colors.grayLight,
            margin: "0 auto 8px",
          }}>
            {challenger.display_name.charAt(0).toUpperCase()}
          </div>
          <div style={{ fontSize: 13, fontWeight: 700, color: colors.white, marginBottom: 2 }}>
            {challenger.display_name}
          </div>
          {challengerLeading && (
            <div style={{ fontSize: 10, fontWeight: 700, color: colors.primary, marginBottom: 6 }}>
              WINNING
            </div>
          )}
          <div style={{
            fontSize: 28, fontWeight: 800, lineHeight: 1,
            color: challengerLeading ? colors.primary : colors.white,
            marginTop: 4, marginBottom: 4,
          }}>
            {cPct}%
          </div>
          <div style={{ fontSize: 12, color: colors.grayLight }}>
            {challenger.current_value} / {challenger.target_value} {unit}
          </div>
          {/* Mini progress bar */}
          <div style={{
            height: 4, borderRadius: 2, background: colors.surfaceLight,
            overflow: "hidden", marginTop: 8,
          }}>
            <div style={{
              height: "100%", borderRadius: 2,
              width: `${Math.min(100, cPct)}%`,
              background: challengerLeading ? colors.primary : colors.grayLight,
              transition: "width 0.4s ease",
            }} />
          </div>
        </div>

        {/* Opponent */}
        <div style={{
          background: opponentLeading ? "rgba(124,58,237,0.12)" : "rgba(255,255,255,0.02)",
          borderRadius: 14, padding: 14, textAlign: "center",
          border: opponentLeading ? `1px solid ${colors.purple}33` : "1px solid transparent",
        }}>
          <div style={{
            width: 40, height: 40, borderRadius: 20,
            background: opponentLeading ? colors.purple : colors.surfaceLight,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 16, fontWeight: 700,
            color: opponentLeading ? colors.white : colors.grayLight,
            margin: "0 auto 8px",
          }}>
            {opponent.display_name.charAt(0).toUpperCase()}
          </div>
          <div style={{ fontSize: 13, fontWeight: 700, color: colors.white, marginBottom: 2 }}>
            {opponent.display_name}
          </div>
          {opponentLeading && (
            <div style={{ fontSize: 10, fontWeight: 700, color: colors.purple, marginBottom: 6 }}>
              WINNING
            </div>
          )}
          <div style={{
            fontSize: 28, fontWeight: 800, lineHeight: 1,
            color: opponentLeading ? colors.purple : colors.white,
            marginTop: 4, marginBottom: 4,
          }}>
            {oPct}%
          </div>
          <div style={{ fontSize: 12, color: colors.grayLight }}>
            {opponent.current_value} / {opponent.target_value} {unit}
          </div>
          {/* Mini progress bar */}
          <div style={{
            height: 4, borderRadius: 2, background: colors.surfaceLight,
            overflow: "hidden", marginTop: 8,
          }}>
            <div style={{
              height: "100%", borderRadius: 2,
              width: `${Math.min(100, oPct)}%`,
              background: opponentLeading ? colors.purple : colors.grayLight,
              transition: "width 0.4s ease",
            }} />
          </div>
        </div>
      </div>

      {/* VS divider with difference */}
      {!tied && (
        <div style={{
          textAlign: "center", marginTop: 12,
          fontSize: 12, color: colors.grayLight,
        }}>
          Difference: {Math.abs(challenger.current_value - opponent.current_value).toFixed(1)} {unit}
        </div>
      )}
    </div>
  );
}

/* ---------- ACTIVITY CHART (adaptive) ---------- */

function ActivityChart({
  activities,
  startDate,
  expiresAt,
  targetValue,
  unit,
}: {
  activities: ActivityEntry[];
  startDate?: string;
  expiresAt?: string;
  targetValue: number;
  unit: string;
}) {
  const isSessions = unit === "sessions" || unit === "runs" || unit === "rides";

  // Calculate bet duration in days
  const start = startDate ? new Date(startDate.length === 10 ? `${startDate}T00:00:00Z` : startDate) : new Date();
  const end = expiresAt ? new Date(expiresAt) : new Date();
  const durationDays = Math.max(1, Math.ceil((end.getTime() - start.getTime()) / 86400000));

  // ≤1 day → Activity List
  if (durationDays <= 1) {
    return <ActivityList activities={activities} unit={unit} isSessions={isSessions} targetValue={targetValue} />;
  }

  // 2+ days → Cumulative area chart
  return (
    <ProgressChart
      activities={activities}
      startDate={startDate}
      targetValue={targetValue}
      unit={unit}
    />
  );
}

/* -- Activity List (≤24h bets) -- */

function ActivityList({
  activities,
  unit,
  isSessions,
  targetValue,
}: {
  activities: ActivityEntry[];
  unit: string;
  isSessions: boolean;
  targetValue: number;
}) {
  const total = activities.reduce((s, a) => s + a.value, 0);

  if (activities.length === 0) {
    return (
      <div style={{
        background: "#0F0F17", borderRadius: 16, padding: 24, marginBottom: 24,
        textAlign: "center",
      }}>
        <div style={{ color: "#9CA3AF", fontSize: 14, marginBottom: 4 }}>No activities recorded yet.</div>
        <div style={{ fontSize: 13 }}>Get moving! 🏃</div>
        <div style={{ color: "#6B7280", fontSize: 12, marginTop: 12 }}>0 / {formatVal(targetValue, isSessions)} {unit}</div>
      </div>
    );
  }

  return (
    <div style={{
      background: "#0F0F17", borderRadius: 16, padding: "16px 16px 12px", marginBottom: 24,
    }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase" as const, marginBottom: 12, letterSpacing: 0.5 }}>
        Activity Log
      </div>

      {activities.map((a, i) => (
        <div key={i} style={{
          display: "flex", alignItems: "center", gap: 12,
          padding: "8px 0",
          borderBottom: i < activities.length - 1 ? "1px solid rgba(255,255,255,0.06)" : "none",
        }}>
          <span style={{ fontSize: 12, color: "#6B7280", fontFamily: "monospace", minWidth: 40 }}>
            {a.time || "—"}
          </span>
          <span style={{ flex: 1, fontSize: 13, color: "#FFFFFF", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" as const }}>
            {a.name || "Activity"}
          </span>
          <span style={{ fontSize: 13, fontWeight: 600, color: "#ADFF00", whiteSpace: "nowrap" as const }}>
            {isSessions ? "1 session" : `${formatVal(a.value, false)} ${unit}`}
          </span>
        </div>
      ))}

      <div style={{ fontSize: 12, color: "#9CA3AF", marginTop: 12 }}>
        Total: {formatVal(total, isSessions)} / {formatVal(targetValue, isSessions)} {unit}
      </div>
    </div>
  );
}

/* -- Progress Chart (2+ days) -- */

function buildCumulativeData(activities: ActivityEntry[], startDate: string) {
  const dailyTotals: Record<string, number> = {};
  for (const a of activities) {
    if (!a.date) continue;
    const key = a.date.slice(0, 10);
    dailyTotals[key] = (dailyTotals[key] || 0) + a.value;
  }
  const days: { key: string; label: string; shortDate: string; daily: number; cumulative: number }[] = [];
  const startKey = startDate.slice(0, 10);
  const todayKey = new Date().toISOString().slice(0, 10);
  let cursor = new Date(`${startKey}T00:00:00Z`);
  const endCursor = new Date(`${todayKey}T00:00:00Z`);
  let cumulative = 0;
  while (cursor.toISOString().slice(0, 10) <= endCursor.toISOString().slice(0, 10) && days.length < 30) {
    const key = cursor.toISOString().slice(0, 10);
    const dayNames = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
    const daily = dailyTotals[key] || 0;
    cumulative += daily;
    days.push({
      key, label: dayNames[cursor.getUTCDay()],
      shortDate: `${cursor.getUTCDate()}/${cursor.getUTCMonth()+1}`,
      daily, cumulative,
    });
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return days;
}

function ProgressChart({ activities, startDate, targetValue, unit }: {
  activities: ActivityEntry[];
  startDate?: string;
  targetValue: number;
  unit: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(320);

  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setWidth(e.contentRect.width);
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  const days = startDate ? buildCumulativeData(activities, startDate) : [];
  const current = days.length > 0 ? days[days.length - 1].cumulative : 0;

  if (days.length === 0) {
    return (
      <div style={{ background: "#0F0F17", borderRadius: 16, padding: "16px 0 14px", overflow: "hidden" }} ref={containerRef}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#6B7280", letterSpacing: "0.08em", padding: "0 16px", marginBottom: 8 }}>PROGRESS</div>
        <div style={{ textAlign: "center", padding: "32px 0", color: "#6B7280", fontSize: 14 }}>
          No activities yet<br />
          <span style={{ fontSize: 20, marginTop: 8, display: "inline-block" }}>🏃</span>
        </div>
        <div style={{ padding: "10px 16px 0" }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: "#FFFFFF" }}>0</span>
          <span style={{ fontSize: 13, color: "#6B7280" }}> / {targetValue} {unit}</span>
        </div>
      </div>
    );
  }

  const chartH = 130;
  const padLeft = 32;
  const padRight = 16;
  const padTop = 20;
  const padBottom = 4;
  const chartW = width - padLeft - padRight;
  const maxY = targetValue * 1.12;

  const toX = (i: number) => padLeft + (i / Math.max(days.length - 1, 1)) * chartW;
  const toY = (val: number) => padTop + (1 - val / maxY) * (chartH - padTop - padBottom);

  const targetY = toY(targetValue);
  const points = days.map((d, i) => ({ x: toX(i), y: toY(d.cumulative) }));

  let linePath = `M ${points[0].x} ${points[0].y}`;
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    const cpx1 = prev.x + (curr.x - prev.x) * 0.4;
    const cpx2 = prev.x + (curr.x - prev.x) * 0.6;
    linePath += ` C ${cpx1} ${prev.y}, ${cpx2} ${curr.y}, ${curr.x} ${curr.y}`;
  }

  const areaPath = linePath + ` L ${points[points.length - 1].x} ${chartH} L ${points[0].x} ${chartH} Z`;
  const lastPt = points[points.length - 1];

  return (
    <div style={{ background: "#0F0F17", borderRadius: 16, padding: "16px 0 14px", overflow: "hidden", width: "100%" }} ref={containerRef}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#6B7280", letterSpacing: "0.08em", padding: "0 16px", marginBottom: 8 }}>PROGRESS</div>

      <svg width={width} height={chartH + 20} style={{ display: "block" }}>
        <defs>
          <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#ADFF00" stopOpacity={0.25} />
            <stop offset="100%" stopColor="#ADFF00" stopOpacity={0.02} />
          </linearGradient>
          <filter id="glow">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>

        <line x1={padLeft} y1={targetY} x2={width - padRight} y2={targetY} stroke="#7C3AED" strokeWidth="1.5" strokeDasharray="6 4" opacity={0.7} />
        <text x={width - padRight} y={targetY - 6} textAnchor="end" fill="#7C3AED" fontSize="10" fontWeight="600">{targetValue} {unit}</text>
        <text x={padLeft - 4} y={chartH - padBottom} textAnchor="end" fill="#4B5563" fontSize="10">0</text>

        <path d={areaPath} fill="url(#areaGrad)" />
        <path d={linePath} fill="none" stroke="#ADFF00" strokeWidth="2.5" strokeLinecap="round" />

        <circle cx={lastPt.x} cy={lastPt.y} r={6} fill="#ADFF00" stroke="#0F0F17" strokeWidth={3} filter="url(#glow)" />
        <circle cx={lastPt.x} cy={lastPt.y} r={3.5} fill="#ADFF00" />

        {days.map((d, i) => (
          <text key={d.key} x={toX(i)} y={chartH + 14} textAnchor="middle" fill="#6B7280" fontSize="10">
            {days.length <= 7 ? d.label : d.shortDate}
          </text>
        ))}
      </svg>

      <div style={{ display: "flex", gap: 6, padding: "12px 16px 0", overflowX: "auto" as const, scrollbarWidth: "none" as const }}>
        {days.map((d, i) => (
          <div key={d.key} style={{
            flex: "0 0 auto", background: "rgba(255,255,255,0.03)", borderRadius: 8,
            padding: "6px 14px", textAlign: "center" as const, minWidth: 56,
            borderLeft: i === days.length - 1 ? "2px solid #ADFF00" : "2px solid transparent",
          }}>
            <div style={{ fontSize: 10, color: "#6B7280", marginBottom: 2 }}>{d.shortDate}</div>
            <div style={{ fontSize: 15, fontWeight: d.daily > 0 ? 700 : 400, color: d.daily > 0 ? "#FFFFFF" : "#3B3B4F" }}>
              {d.daily > 0 ? d.daily.toFixed(1) : "—"}
            </div>
            {d.daily > 0 && <div style={{ fontSize: 9, color: "#6B7280", marginTop: -1 }}>{unit}</div>}
          </div>
        ))}
      </div>

      <div style={{ padding: "10px 16px 0" }}>
        <span style={{ fontSize: 13, color: "#9CA3AF" }}>Total: </span>
        <span style={{ fontSize: 14, fontWeight: 700, color: "#FFFFFF" }}>{current.toFixed(1)}</span>
        <span style={{ fontSize: 13, color: "#6B7280" }}> / {targetValue} {unit}</span>
      </div>
    </div>
  );
}

function formatVal(v: number, isSessions: boolean): string {
  if (isSessions) return Math.round(v).toString();
  if (v >= 100) return Math.round(v).toString();
  if (v % 1 === 0) return v.toString();
  return v.toFixed(1);
}

function InfoChip({ emoji, label, sub }: { emoji: string; label: string; sub: string }) {
  return (
    <div style={{
      background: colors.surface, border: `1px solid ${colors.border}`,
      borderRadius: 12, padding: "10px 8px", textAlign: "center",
    }}>
      <div style={{ fontSize: 14, marginBottom: 2 }}>{emoji}</div>
      <div style={{ fontSize: 14, fontWeight: 700, color: colors.white }}>{label}</div>
      <div style={{ fontSize: 11, color: colors.grayLight }}>{sub}</div>
    </div>
  );
}

function getUnit(stravaBetType: string | null): string {
  if (stravaBetType === "distance") return "km";
  if (stravaBetType === "sessions") return "sessions";
  if (stravaBetType === "steps") return "steps";
  return "";
}

/* ---------- RESOLVED ---------- */

function ResolvedState({ bet, user, navigate, betId }: BetStateProps) {
  const isPileOn = bet.bet_mode === "pile_on";
  const participants = useParticipants(bet, betId);
  const won = !!user && (() => {
    if (isPileOn && participants && bet.winner_side) {
      const inFor = participants.for_side?.some((p) => p.telegram_id === user.telegram_id);
      const inAgainst = participants.against_side?.some((p) => p.telegram_id === user.telegram_id);
      const mySide = inFor ? "for" : inAgainst ? "against" : null;
      return mySide === bet.winner_side;
    }
    return bet.winner_id === user.id || bet.winner_id === user.telegram_id;
  })();
  const tonAmount = bet.amount / 1_000_000_000;
  const tonDisplay = tonAmount % 1 === 0 ? tonAmount.toFixed(1) : tonAmount.toString();
  const potTon = isPileOn ? (bet.total_pot || bet.amount) / 1_000_000_000 : tonAmount * 2;
  // VEN-52 / VEN-60: for 1v1 the winner received the full pot minus escrow
  // gas (~0.01 TON), e.g. 0.19 TON on a 0.1 TON stake — matches the group
  // notification and the wallet receipt. Showing the 0.1 stake here was the
  // loser's number, which made the winner think they got less than they did.
  const fmtTon = (n: number) => {
    const s = n.toFixed(2);
    return s.endsWith("0") ? s.slice(0, -1) : s;
  };
  const winnerPayoutTon = isPileOn
    ? potTon
    : Math.max(0, (bet.amount * 2 - 10_000_000) / 1_000_000_000);
  const winnerDisplay = isPileOn ? fmtTon(winnerPayoutTon) : fmtTon(winnerPayoutTon);
  const loserDisplay = tonDisplay;
  const cleanedDescription = cleanDescription(bet.description);
  const emoji = getActivityEmoji(cleanedDescription);
  const isCreator = user?.telegram_id === bet.challenger_telegram_id || user?.id === (bet as any).challenger_id;
  const opponentName = isCreator
    ? (bet.opponent_name || "opponent").replace(/^@/, "")
    : (bet.challenger_name || "challenger").replace(/^@/, "");

  const taglines = ["get rekt. 😈", "friendship destroyed. 💀", "talk is cheap. 🧾"];
  const tagline = taglines[Math.floor(Math.random() * taglines.length)];
  const betIdShort = bet.bet_id.slice(-4).toUpperCase();

  const handleClose = () => {
    navigate("/");
  };

  const handleSendToGroup = () => {
    // VEN-40: Telegram Desktop cannot resolve the `t.me/<bot>/app?startapp=…`
    // Mini App deep-link on most builds and shows "Bot Application Not
    // Found". For a shared *result* the recipient only needs to see the
    // outcome, so send the public victory-card web URL instead — it works
    // in every client (Desktop browser, Telegram in-app browser, Mobile).
    //
    // VEN-39 (tier 1): replace the generic "SideBet result!" text with a
    // win/lose-specific line that mirrors the screen — description, amount,
    // and a nudge for the recipient to rematch.
    const shareText = won
      ? `🏆 I won ${winnerDisplay} TON on "${cleanedDescription}" — SideBets`
      : `🤡 I lost ${loserDisplay} TON on "${cleanedDescription}" — want a rematch?`;
    // VEN-39 round 3: differentiate winner/loser via the URL *path* instead
    // of a `?from=` query param. Round 2 used ?from=winner|loser but QA
    // reported the shared URL looked identical in both cases — Telegram's
    // link preview appears to drop or collapse query strings in some
    // clients, so the recipient always saw the bare `/victory` URL.
    // Path segments (`/victory` vs `/defeat`) survive preview rewriting.
    const outcomePath = won ? "victory" : "defeat";
    const shareUrl = encodeURIComponent(
      `https://sidebets.vercel.app/bet/${bet.bet_id}/${outcomePath}`,
    );
    const tgShareUrl = `https://t.me/share/url?url=${shareUrl}&text=${encodeURIComponent(shareText)}`;
    const tg = window.Telegram?.WebApp;
    if (tg?.openTelegramLink) {
      tg.openTelegramLink(tgShareUrl);
    } else {
      // Desktop fallback: open the share sheet in a regular tab when
      // Telegram WebApp's `openTelegramLink` is unavailable (e.g. browser
      // preview of the Mini App).
      window.open(tgShareUrl, "_blank", "noopener");
    }
  };

  const handleBetAgain = () => {
    navigate("/create");
  };

  return (
    <div style={{
      display: "flex", flexDirection: "column", height: "100%",
      background: colors.bg,
    }}>
      {/* Close button (top-right) */}
      <div style={{ display: "flex", justifyContent: "flex-end", padding: "12px 16px" }}>
        <button onClick={handleClose} style={{ fontSize: 18, color: colors.gray, padding: 4 }}>
          ✕
        </button>
      </div>

      <div style={{
        flex: 1, overflow: "auto", padding: "0 16px 32px",
        display: "flex", flexDirection: "column", alignItems: "center",
      }}>
        {/* Hero emoji */}
        <div style={{ fontSize: 56, marginBottom: 12 }}>{won ? "🏆" : "🤡"}</div>

        {/* Title */}
        <div style={{ fontSize: 28, fontWeight: 800, marginBottom: 8 }}>
          {won ? "YOU WON" : "YOU LOST"}
        </div>

        {/* Amount */}
        <div style={{
          fontSize: 36, fontWeight: 800, marginBottom: 4,
          color: won ? colors.success : colors.danger,
        }}>
          {won ? `+${winnerDisplay}` : `-${loserDisplay}`} TON
        </div>

        {/* Victory card */}
        <div style={{
          width: "100%",
          background: "#0F0F17",
          border: `1px solid ${colors.border}`,
          borderRadius: 20,
          padding: 20,
          marginTop: 24,
          marginBottom: 32,
        }}>
          {/* Bet ID badge */}
          <div style={{ textAlign: "center", marginBottom: 16 }}>
            <span style={{
              fontSize: 11, fontWeight: 700, color: colors.grayLight,
              background: colors.surface, padding: "4px 10px", borderRadius: 8,
            }}>
              #BET-{betIdShort}
            </span>
          </div>

          {isPileOn && participants ? (
            <>
              {/* Pile on: show winner side header */}
              <div style={{ textAlign: "center", fontSize: 18, fontWeight: 800, marginBottom: 16 }}>
                {bet.winner_side === "for" ? "💪 FOR SIDE WINS!" : "😈 AGAINST SIDE WINS!"}
              </div>

              {/* Bet description + pot */}
              <div style={{ textAlign: "center", fontSize: 13, color: colors.grayLight, marginBottom: 4 }}>
                {emoji} {cleanedDescription}
              </div>
              <div style={{ textAlign: "center", fontSize: 13, color: colors.grayLight, marginBottom: 16 }}>
                Pot: {potTon.toFixed(1)} TON
              </div>

              {/* Winners */}
              <ParticipantsList
                participants={participants}
                betStatus="resolved"
                winnerSide={bet.winner_side}
              />
            </>
          ) : (
            <>
              {/* 1v1: Two columns: YOU vs Opponent */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
                <div style={{
                  background: won ? "rgba(0, 200, 83, 0.12)" : "rgba(255, 59, 48, 0.12)",
                  borderRadius: 14, padding: 14, textAlign: "center",
                }}>
                  <div style={{ fontSize: 24, marginBottom: 4 }}>{won ? "🏆" : "🤡"}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: colors.white, marginBottom: 2 }}>YOU</div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: won ? colors.success : colors.danger }}>
                    {won ? "CHAMPION" : "REKT"}
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: won ? colors.success : colors.danger, marginTop: 4 }}>
                    {won ? `+${winnerDisplay}` : `-${loserDisplay}`} TON
                  </div>
                </div>
                <div style={{
                  background: won ? "rgba(255, 59, 48, 0.12)" : "rgba(0, 200, 83, 0.12)",
                  borderRadius: 14, padding: 14, textAlign: "center",
                }}>
                  <div style={{ fontSize: 24, marginBottom: 4 }}>{won ? "🤡" : "🏆"}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: colors.white, marginBottom: 2 }}>@{opponentName}</div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: won ? colors.danger : colors.success }}>
                    {won ? "REKT" : "CHAMPION"}
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: won ? colors.danger : colors.success, marginTop: 4 }}>
                    {won ? `-${loserDisplay}` : `+${winnerDisplay}`} TON
                  </div>
                </div>
              </div>

              {/* Bet description */}
              <div style={{ textAlign: "center", fontSize: 13, color: colors.grayLight, marginBottom: 4 }}>
                {emoji} {cleanedDescription}
              </div>
            </>
          )}

          {bet.strava_bet_type && (
            <div style={{ textAlign: "center", fontSize: 12, color: colors.success, marginBottom: 8 }}>
              ✅ Verified by HealthBridge
            </div>
          )}

          {/* Tagline */}
          <div style={{ textAlign: "center", fontSize: 12, color: colors.gray, marginTop: 8 }}>
            {tagline}
          </div>
        </div>

        {/* Spacer */}
        <div style={{ flex: 1 }} />

        {/* Send to Group — GREEN (money action) */}
        <button onClick={handleSendToGroup} style={{
          width: "100%", padding: "14px 0", borderRadius: 14, border: "none",
          background: colors.primary, color: colors.bg,
          fontSize: 15, fontWeight: 700, marginBottom: 12, cursor: "pointer",
        }}>
          🔥 Send to Group
        </button>

        {/* Bet Again — purple */}
        <button onClick={handleBetAgain} style={{
          width: "100%", padding: "14px 0", borderRadius: 14, border: "none",
          background: colors.purple, color: colors.white,
          fontSize: 15, fontWeight: 700, marginBottom: 16, cursor: "pointer",
        }}>
          🔄 Bet Again
        </button>

        {/* TX info */}
        <div style={{ fontSize: 11, color: colors.gray, textAlign: "center" }}>
          TX: UQC9...w69Y · confirmed
        </div>
      </div>
    </div>
  );
}

/* ---------- PARTICIPANTS LIST (pile_on) ---------- */

function ParticipantsList({
  participants,
  betStatus,
  winnerSide,
}: {
  participants: ParticipantsData;
  betStatus: string;
  winnerSide?: string;
}) {
  const isResolved = betStatus === "resolved";
  const forTon = participants.for_total / 1_000_000_000;
  const againstTon = participants.against_total / 1_000_000_000;

  const renderSide = (
    side: Participant[],
    label: string,
    sideKey: string,
    totalTon: number,
    sideEmoji: string,
    accentColor: string,
  ) => (
    <div style={{ marginBottom: 16 }}>
      <div style={{
        fontSize: 13, fontWeight: 700,
        color: isResolved && winnerSide === sideKey ? colors.success : accentColor,
        marginBottom: 8,
      }}>
        {sideEmoji} {isResolved ? (winnerSide === sideKey ? "WINNERS" : "LOSERS") : label}
        {" — "}{totalTon.toFixed(1)} TON ({side.length})
      </div>
      {side.map((p) => {
        const pTon = p.amount / 1_000_000_000;
        const isWinner = isResolved && winnerSide === sideKey;
        const profit = isWinner && p.payout_amount ? (p.payout_amount - p.amount) / 1_000_000_000 : 0;
        return (
          <div key={p.user_id} style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "6px 0",
            borderLeft: `3px solid ${accentColor}33`,
            paddingLeft: 10,
            marginBottom: 2,
          }}>
            <span style={{ fontSize: 13, color: colors.white }}>
              {p.display_name}
            </span>
            <span style={{ fontSize: 13, color: colors.grayLight }}>
              {pTon.toFixed(1)} TON
              {isResolved && (
                isWinner
                  ? <span style={{ color: colors.success, marginLeft: 6 }}>+{profit.toFixed(2)} 🎉</span>
                  : <span style={{ color: colors.danger, marginLeft: 6 }}>-{pTon.toFixed(1)} 🤡</span>
              )}
            </span>
          </div>
        );
      })}
    </div>
  );

  return (
    <div style={{
      width: "100%", background: colors.surface, border: `1px solid ${colors.border}`,
      borderRadius: 16, padding: 16, marginBottom: 16,
    }}>
      {renderSide(participants.for_side, "FOR", "for", forTon, "💪", colors.success)}
      {renderSide(participants.against_side, "AGAINST", "against", againstTon, "😈", colors.danger)}
    </div>
  );
}

/* ---------- Helper ---------- */

function getActivityEmoji(description: string | undefined | null): string {
  if (!description) return "🎯";
  const d = description.toLowerCase();
  if (d.includes("run")) return "🏃";
  if (d.includes("cycle") || d.includes("ride") || d.includes("bike")) return "🚴";
  if (d.includes("swim")) return "🏊";
  if (d.includes("workout") || d.includes("gym") || d.includes("session")) return "💪";
  if (d.includes("step")) return "👟";
  return "🎯";
}
